import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { notificationsApi, type UserNotificationItem, type UserNotificationQueryDto } from '@/services/api/notifications'
import { useAuthStore } from '@/stores/auth'

const GLOBAL_READ_STORAGE_PREFIX = 'notification_global_reads:'
const REALTIME_REFRESH_DEBOUNCE_MS = 250
const MAX_REALTIME_DEDUPE_KEYS = 500

type GlobalReadMap = Record<string, string>

export interface NotificationRealtimeEventPayload {
  notificationId?: string
  messageId?: string
  timestamp?: string
  type?: string
  ticketId?: string
}

export const useUserNotificationsStore = defineStore('userNotifications', () => {
  const authStore = useAuthStore()

  const items = ref<UserNotificationItem[]>([])
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  })
  const unreadCountServer = ref(0)
  const unreadCountDisplay = ref(0)
  const isLoading = ref(false)
  const isUnreadLoading = ref(false)
  const initializedForUserId = ref<string | null>(null)

  const globalReadMap = ref<GlobalReadMap>({})

  const processedRealtimeKeys = new Set<string>()
  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  const currentUserId = computed(() => authStore.user?.id ?? null)

  const getGlobalReadStorageKey = (userId: string) => `${GLOBAL_READ_STORAGE_PREFIX}${userId}`

  const loadGlobalReadMap = (userId: string) => {
    try {
      const raw = localStorage.getItem(getGlobalReadStorageKey(userId))
      globalReadMap.value = raw ? (JSON.parse(raw) as GlobalReadMap) : {}
    } catch {
      globalReadMap.value = {}
    }
  }

  const persistGlobalReadMap = (userId: string) => {
    localStorage.setItem(getGlobalReadStorageKey(userId), JSON.stringify(globalReadMap.value))
  }

  const isNotificationReadDisplay = (notification: UserNotificationItem) => {
    if (!notification.isGlobal) {
      return Boolean(notification.isRead)
    }

    return Boolean(globalReadMap.value[notification.id])
  }

  const syncDisplayFields = () => {
    items.value = items.value.map((notification) => {
      const isReadDisplay = isNotificationReadDisplay(notification)
      return {
        ...notification,
        isReadDisplay,
        status: isReadDisplay ? 'READ' : 'UNREAD',
      }
    })
  }

  const recomputeUnreadCountDisplay = () => {
    const unreadGlobalClient = items.value.filter(
      (notification) => notification.isGlobal && !isNotificationReadDisplay(notification),
    ).length

    unreadCountDisplay.value = unreadCountServer.value + unreadGlobalClient
  }

  const refreshMyNotifications = async (query?: UserNotificationQueryDto) => {
    if (!currentUserId.value) return

    isLoading.value = true

    try {
      const response = await notificationsApi.getMyNotifications({
        page: query?.page ?? pagination.value.page,
        limit: query?.limit ?? pagination.value.limit,
        type: query?.type,
        isRead: query?.isRead,
      })

      items.value = response.data || []
      pagination.value = response.pagination || {
        page: 1,
        limit: query?.limit ?? 20,
        total: 0,
        pages: 1,
      }

      syncDisplayFields()
      recomputeUnreadCountDisplay()
    } catch (error) {
      console.error('Failed to refresh user notifications list', error)
    } finally {
      isLoading.value = false
    }
  }

  const refreshUnreadCount = async () => {
    if (!currentUserId.value) return

    isUnreadLoading.value = true

    try {
      const response = await notificationsApi.getMyUnreadCount()
      unreadCountServer.value = response.unreadCountServer ?? response.unreadCount ?? 0
      recomputeUnreadCountDisplay()
    } catch (error) {
      console.error('Failed to refresh unread notifications count', error)
    } finally {
      isUnreadLoading.value = false
    }
  }

  const refreshAll = async (query?: UserNotificationQueryDto) => {
    await Promise.all([refreshMyNotifications(query), refreshUnreadCount()])
  }

  const markGlobalAsReadLocal = (notificationId: string) => {
    const userId = currentUserId.value
    if (!userId) return

    if (!globalReadMap.value[notificationId]) {
      globalReadMap.value = {
        ...globalReadMap.value,
        [notificationId]: new Date().toISOString(),
      }
      persistGlobalReadMap(userId)
    }
  }

  const markAsRead = async (notificationId: string) => {
    const notificationIndex = items.value.findIndex((item) => item.id === notificationId)
    if (notificationIndex === -1) {
      await refreshAll()
      return
    }

    const notification = items.value[notificationIndex]

    if (notification.isGlobal) {
      markGlobalAsReadLocal(notificationId)
      syncDisplayFields()
      recomputeUnreadCountDisplay()
      return
    }

    if (notification.isRead) {
      return
    }

    // Optimistic update: reflect read-state immediately when user clicks.
    items.value = items.value.map((item) => {
      if (item.id !== notificationId) return item
      return {
        ...item,
        isRead: true,
        status: 'READ',
      }
    })

    syncDisplayFields()
    recomputeUnreadCountDisplay()

    try {
      await notificationsApi.markUserNotificationAsRead(notificationId)
      await refreshUnreadCount()
      recomputeUnreadCountDisplay()
    } catch (error) {
      // Keep optimistic UI state; next refresh will reconcile with server truth.
      console.error('Failed to mark user notification as read', error)
      throw error
    }
  }

  const markAllAsRead = async () => {
    if (!currentUserId.value) return

    await notificationsApi.markAllMyNotificationsAsRead()

    const now = new Date().toISOString()
    const nextGlobalReadMap: GlobalReadMap = { ...globalReadMap.value }

    items.value.forEach((notification) => {
      if (notification.isGlobal) {
        nextGlobalReadMap[notification.id] = nextGlobalReadMap[notification.id] ?? now
      }
    })

    globalReadMap.value = nextGlobalReadMap
    persistGlobalReadMap(currentUserId.value)

    items.value = items.value.map((notification) => ({
      ...notification,
      isRead: notification.isGlobal ? notification.isRead : true,
      status: 'READ',
    }))

    syncDisplayFields()
    await refreshUnreadCount()
    recomputeUnreadCountDisplay()
  }

  const buildRealtimeDedupeKey = (payload: NotificationRealtimeEventPayload) => {
    if (payload.notificationId) return `notification:${payload.notificationId}`
    if (payload.messageId) return `message:${payload.messageId}`

    return `fallback:${payload.type ?? 'notification'}:${payload.ticketId ?? ''}:${payload.timestamp ?? ''}`
  }

  const scheduleRefreshFromRealtime = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
    }

    refreshTimer = setTimeout(() => {
      void refreshAll()
      refreshTimer = null
    }, REALTIME_REFRESH_DEBOUNCE_MS)
  }

  const handleRealtimeEvent = (payload: NotificationRealtimeEventPayload) => {
    const dedupeKey = buildRealtimeDedupeKey(payload)

    if (processedRealtimeKeys.has(dedupeKey)) {
      return false
    }

    processedRealtimeKeys.add(dedupeKey)

    if (processedRealtimeKeys.size > MAX_REALTIME_DEDUPE_KEYS) {
      processedRealtimeKeys.clear()
      processedRealtimeKeys.add(dedupeKey)
    }

    scheduleRefreshFromRealtime()
    return true
  }

  const initializeForCurrentUser = async () => {
    const userId = currentUserId.value
    if (!userId) {
      resetState()
      return
    }

    if (initializedForUserId.value !== userId) {
      loadGlobalReadMap(userId)
      initializedForUserId.value = userId
    }

    await refreshAll({ page: 1, limit: pagination.value.limit || 20 })
  }

  const resetState = () => {
    items.value = []
    pagination.value = {
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    }
    unreadCountServer.value = 0
    unreadCountDisplay.value = 0
    globalReadMap.value = {}
    initializedForUserId.value = null

    processedRealtimeKeys.clear()

    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  return {
    items,
    pagination,
    unreadCountServer,
    unreadCountDisplay,
    isLoading,
    isUnreadLoading,
    initializeForCurrentUser,
    refreshMyNotifications,
    refreshUnreadCount,
    refreshAll,
    markAsRead,
    markAllAsRead,
    isNotificationReadDisplay,
    handleRealtimeEvent,
    resetState,
  }
})
